import { useEffect, useRef, useState } from 'react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000/api/v1';

interface WaitlistEntry {
  id: string;
  email: string;
  name: string | null;
  source: string | null;
  created_at: string;
}

interface Pagination {
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function downloadCsv(entries: WaitlistEntry[]) {
  const header = 'Name,Email,Source,Joined\n';
  const rows = entries
    .map(e =>
      [
        e.name ? `"${e.name.replace(/"/g, '""')}"` : '',
        `"${e.email}"`,
        e.source ?? '',
        formatDate(e.created_at),
      ].join(',')
    )
    .join('\n');

  const blob = new Blob([header + rows], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `filmspo-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Auth Gate ────────────────────────────────────────────────

interface AuthGateProps {
  onUnlock: (password: string) => void;
}

function AuthGate({ onUnlock }: AuthGateProps) {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(false);

    // Validate password against the API directly
    try {
      const res = await fetch(`${API_URL}/waitlist?page=1&pageSize=1`, {
        headers: { Authorization: `Bearer ${value}` },
      });
      if (res.ok) {
        onUnlock(value);
      } else {
        setError(true);
        setValue('');
      }
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="wadmin-gate">
      <form onSubmit={handleSubmit} className="wadmin-gate-form">
        <h1>Waitlist Admin</h1>
        <label htmlFor="wadmin-pass">Password</label>
        <input
          ref={inputRef}
          id="wadmin-pass"
          type="password"
          value={value}
          onChange={e => {
            setValue(e.target.value);
            setError(false);
          }}
          placeholder="Enter admin password"
          autoComplete="current-password"
        />
        {error && <p className="wadmin-error">Incorrect password.</p>}
        <button
          type="submit"
          className="button button-gold wadmin-submit"
          disabled={loading || !value}
        >
          {loading ? 'Checking…' : 'Unlock'}
        </button>
      </form>
    </div>
  );
}

// ─── Admin Dashboard ──────────────────────────────────────────

interface AdminDashboardProps {
  password: string;
}

function AdminDashboard({ password }: AdminDashboardProps) {
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  async function fetchEntries(p: number) {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/waitlist?page=${p}&pageSize=50`, {
        headers: { Authorization: `Bearer ${password}` },
      });
      if (!res.ok) {
        const json = await res.json().catch(() => ({}));
        setError(json.error?.message || `Error ${res.status}`);
        setLoading(false);
        return;
      }
      const json = await res.json();
      setEntries(json.data);
      setPagination(json.pagination);
    } catch {
      setError('Failed to connect to API.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchEntries(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handlePageChange(p: number) {
    setPage(p);
    fetchEntries(p);
  }

  return (
    <div className="wadmin-shell">
      <div className="wadmin-header">
        <div>
          <h1>Waitlist</h1>
          {pagination && (
            <p className="wadmin-count">
              {pagination.totalItems} {pagination.totalItems === 1 ? 'signup' : 'signups'} total
            </p>
          )}
        </div>
        {entries.length > 0 && (
          <button
            type="button"
            className="button button-gold button-compact"
            onClick={() => downloadCsv(entries)}
          >
            Export CSV
          </button>
        )}
      </div>

      {loading && <p className="wadmin-status">Loading…</p>}
      {error && <p className="wadmin-error wadmin-status">{error}</p>}

      {!loading && !error && entries.length === 0 && (
        <p className="wadmin-status">No signups yet.</p>
      )}

      {!loading && entries.length > 0 && (
        <>
          <div className="wadmin-table-wrap">
            <table className="wadmin-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Source</th>
                  <th>Joined</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, i) => (
                  <tr key={entry.id}>
                    <td className="wadmin-num">{(page - 1) * 50 + i + 1}</td>
                    <td>{entry.email}</td>
                    <td>{entry.name ?? <span className="wadmin-empty">—</span>}</td>
                    <td>{entry.source ?? <span className="wadmin-empty">—</span>}</td>
                    <td className="wadmin-date">{formatDate(entry.created_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination && pagination.totalPages > 1 && (
            <div className="wadmin-pagination">
              <button type="button" disabled={page <= 1} onClick={() => handlePageChange(page - 1)}>
                ← Prev
              </button>
              <span>
                Page {page} of {pagination.totalPages}
              </span>
              <button
                type="button"
                disabled={page >= pagination.totalPages}
                onClick={() => handlePageChange(page + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────

export function WaitlistAdminPage() {
  const [password, setPassword] = useState<string | null>(null);

  if (!password) {
    return <AuthGate onUnlock={setPassword} />;
  }

  return <AdminDashboard password={password} />;
}
